import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';

const differentiators = [
  { num: '01', title: 'Multi-material recovery expertise' },
  { num: '02', title: 'Transparent & traceable waste tracking' },
  { num: '03', title: 'Compliance-ready systems' },
  { num: '04', title: 'Circular economy focused operations' },
  { num: '05', title: 'ESG reporting support' },
];

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden" id="hero">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source srcSet="/images/hero-bg.webp" type="image/webp" />
          <img
            src="/images/hero-bg.webp"
            alt="Waste recovery operations"
            className="w-full h-full object-cover"
            loading="eager"
            {...{ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>}
            width={1600}
            height={1200}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/60 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {/* Left — editorial content */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-p-400 mb-6">
              Patekar Recycle Solution Pvt Ltd · Punarvaa™
            </p>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.95] mb-6">
              We Don't Just<br />
              <span className="text-p-400">Collect Waste.</span><br />
              We Recreate Value.
            </h1>

            <p className="text-xl text-gray-300 mb-3">
              For businesses. For cities. For the planet.
            </p>

            <p className="text-base text-gray-400 leading-relaxed max-w-lg mb-10">
              Punarvaa builds structured systems to recover value from waste. We design compliant
              waste management, recycling, and resource recovery solutions for industries, retailers,
              and communities across India.
            </p>

            {/* Flow statement */}
            <div className="flex items-center gap-3 mb-10">
              {['Waste', 'Resource', 'Value'].map((word, i) => (
                <div key={word} className="flex items-center gap-3">
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full backdrop-blur-sm ${
                    i === 0 ? 'bg-gray-700 text-gray-300' :
                    i === 1 ? 'bg-p-800 text-p-200' :
                    'bg-p-600 text-white'
                  }`}>{word}</span>
                  {i < 2 && <ArrowRight className="w-4 h-4 text-gray-500" />}
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/contact?audit=true"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-p-600 text-white text-sm font-semibold rounded-md hover:bg-p-700 transition-colors shadow-lg shadow-p-600/30"
              >
                Request Waste Assessment
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/30 backdrop-blur-sm text-white text-sm font-semibold rounded-md hover:bg-white/15 transition-colors"
              >
                Explore Services
              </Link>
              <Link
                to="/contact?quote=true"
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-p-400 transition-colors"
              >
                Schedule Consultation →
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-0 divide-x divide-gray-700">
              {['8+ Years', '8000 MT Capacity', 'Pan-India'].map((stat) => (
                <div key={stat} className="px-6 first:pl-0">
                  <span className="text-sm font-medium text-gray-300">{stat}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — dark panel with numbered list */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-8">
              <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-6">
                Why Businesses Choose Us
              </p>
              <div className="space-y-0">
                {differentiators.map((item, i) => (
                  <div
                    key={item.num}
                    className={`flex items-start gap-4 py-4 ${i < differentiators.length - 1 ? 'border-b border-gray-800' : ''}`}
                  >
                    <span className="text-sm font-bold text-p-500 mt-0.5 w-6 shrink-0">{item.num}</span>
                    <span className="text-sm font-medium text-gray-200 leading-snug">{item.title}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <p className="text-xs text-gray-500 italic">
                  "Waste management needs structure." — Punarvaa
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
      >
        <ChevronDown className="w-6 h-6 text-gray-400" />
      </motion.div>
    </section>
  );
}
